import React, { useState } from 'react';
import {
  BubbleFormActions,
  BubbleFormFooter,
  BubbleFormHeader,
  FormPageComponentHelpfulProps,
} from '@go-client/components/BubbleUI/BubbleForm';
import ReservationFormik, {
  ReservationFormikForm,
} from '@go-client/components/Formik/ReservationFormik';
import { FormikProps } from 'formik';
import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import { User } from '@go-shared/types/user';
import { ReservationFormMode } from '../ReservationPath';
import { Reservation } from '@go-shared/types/reservation';
import BubbleButton from '@go-client/components/BubbleUI/BubbleButton';
import { sendConfirmationEmail } from '@go-client/lib/api/reservation';
import { BubbleOverlay } from '@go-client/components/BubbleUI';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import { formatCentsToUSD } from '@go-shared/lib/utils';
import { getReservationRevenue } from '@go-shared/lib/reserve';
import { canManageReservation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { calculateReservationFormikPricing } from '@go-client/lib/reserve';

interface EnterReservationPagePropsBase {
  formik: FormikProps<ReservationFormikForm>;
  mode: ReservationFormMode;
  users: User[];
  selectedEventSnapshot: ReservableEventTemplateSnapshot;
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  onSubmitReservationForm: () => void;
  onRefreshReservation?: (reservation: Reservation) => void;
  onNavigateToReservation?: (reservation: Reservation) => void;
}

type EnterReservationPageProps = EnterReservationPagePropsBase & FormPageComponentHelpfulProps;

export default function EnterReservationPage(props: EnterReservationPageProps) {
  const [emailSent, setEmailSent] = useState<boolean | null>(false);
  const [confirmSendEmail, setConfirmSendEmail] = useState(false);
  const { user } = useUserInfo();

  const canEdit = !props.mode.edit || canManageReservation(user, props.mode.edit);
  const disabled = !canEdit || !props.formik.dirty || !props.formik.isValid || props.isBusy;

  function handleEmailClick() {
    if (!confirmSendEmail) {
      setConfirmSendEmail(true);
      return;
    }

    setEmailSent(null);
    try {
      sendConfirmationEmail(props.mode.edit as Reservation);
      setEmailSent(true);
    } catch (err) {
      setEmailSent(false);
      throw err;
    }
  }

  return (
    <div className="eaf-2">
      <FormGenericContainer
        footer={() => (
          <>
            <BubbleFormFooter
              className="text-slate-dark mr-auto"
              items={[
                { label: 'Event Selected', value: props.selectedEventSnapshot.title },
                {
                  label: 'Reservation Selected',
                  value: props.mode.edit
                    ? `${props.mode.edit.reservationCode}${props.mode.edit.cartCode ? ` (${props.mode.edit.cartCode})` : ''}`
                    : undefined,
                },
                {
                  label: 'Payment Summary',
                  value: !props.mode.edit
                    ? formatCentsToUSD(
                        calculateReservationFormikPricing(
                          props.formik.values,
                          props.selectedEventSnapshot
                        ).finalTotal
                      )
                    : undefined,
                },
                {
                  label: 'Total Revenue',
                  value: props.mode.edit
                    ? formatCentsToUSD(getReservationRevenue(props.mode.edit))
                    : undefined,
                },
                {
                  label: 'Last Updated By',
                  value: props.mode.edit?.metadata?.lastUpdatedByName,
                },
              ]}
            />
            <div className="flex items-center">
              {canEdit && (
                <BubbleOverlay loading={emailSent === null}>
                  <BubbleButton
                    onClick={handleEmailClick}
                    variant={confirmSendEmail && !emailSent ? 'crimson' : 'slate'}
                    inverse
                    disabled={!!emailSent || props.formik.dirty}
                  >
                    {emailSent
                      ? 'Email Sent!'
                      : !confirmSendEmail
                        ? 'Send Confirmation Email'
                        : 'Are you sure? Click to Send'}
                  </BubbleButton>
                </BubbleOverlay>
              )}
              <BubbleFormActions
                isBusy={props.isBusy}
                forwardTitle={props.forwardTitle || 'Finish: Save Changes'}
                onGoForward={props.onSubmitReservationForm}
                onGoBack={props.onGoBack}
                forwardDisabled={disabled}
              />
            </div>
          </>
        )}
      >
        <BubbleFormHeader
          title={props.title || 'Manage Reservation'}
          subtitle={props.subtitle || 'Enter Reservation Details'}
          pageIndex={props.pageIndex}
          onClose={props.onClose}
          appendToCloseButton={() => (
            <BubbleButton
              variant="navy"
              inverse
              onClick={() =>
                props.mode.edit ? props.onRefreshReservation?.(props.mode.edit) : null
              }
              icon="rotate"
              iconSize="xl"
            />
          )}
        />
        <ReservationFormik
          type={!canEdit ? 'view' : props.mode.type}
          users={props.users}
          reservation={props.mode.edit}
          selectedEventSnapshot={props.selectedEventSnapshot}
          extManagedFormik={props.formik}
          subnavClassName="-mx-8"
          actionsAppearance="drawer-float"
          onRefreshReservation={props.onRefreshReservation}
          onNavigateToReservation={props.onNavigateToReservation}
          defaultView={
            props.mode.type === 'edit' && props.mode.goToPayments ? 'payments' : 'details'
          }
        />
      </FormGenericContainer>
    </div>
  );
}
